import { SdkContext } from '../internal'
import { noteRowFrom, noteRowVersion, overrideRow, unmodifiedSince, versionedRowWrite } from '@/lib/realtime/rowVersion'

/** Keeps the override's version and each language's (`versions[].updatedAt`) from an answer. */
const noteOverride = <T,>(emailKey: string, body: T): T => {
  const b = body as { updatedAt?: unknown; versions?: { language?: string; updatedAt?: unknown }[] } | null
  noteRowVersion(overrideRow(emailKey), b?.updatedAt)
  for (const v of Array.isArray(b?.versions) ? b!.versions : []) if (v?.language) noteRowVersion(overrideRow(emailKey, v.language), v.updatedAt)
  return body
}
import type { BlockSaveResult, CampaignBlock } from '@/types/campaigns'
import type { DefaultEmailKey, DefaultEmailVersion, EmailDraft, EmailUsages, EmailVersion, LibraryEmail, RenderedEmail } from '@/types/emails'

interface Page<T> {
  items: T[]
  pagination: { page: number; limit: number; total: number; totalPages: number } | null
}

type VersionInput = Omit<EmailVersion, 'language' | 'updatedAt'>
/** F-174: a version save without `blocks` keeps the stored ones (a subject/preheader save never meets a block lock). */
type VersionWrite = Omit<VersionInput, 'blocks'> & { blocks?: VersionInput['blocks'] }

/** F-140: the e-mail library (`/emails`, the campaign team) and the default e-mails (`/email-overrides`, owner and admin). */
export class EmailsResource {
  constructor(private ctx: SdkContext) {}

  async list(params: { usage?: 'FREE' | 'DEFAULT_OVERRIDE'; q?: string; page?: number; limit?: number } = {}): Promise<Page<LibraryEmail>> {
    const res = await this.ctx.raw<unknown>({ httpMethod: 'GET', path: '/emails' }, { query: params })
    /* `raw` gives `{ items, pagination }` for a paginated answer; a bare list otherwise. */
    if (Array.isArray(res)) return { items: res as LibraryEmail[], pagination: null }
    const r = res as { items?: LibraryEmail[]; data?: LibraryEmail[]; pagination?: Page<LibraryEmail>['pagination'] } | null
    return { items: r?.items ?? r?.data ?? [], pagination: r?.pagination ?? null }
  }

  async get(id: string): Promise<LibraryEmail> {
    return this.ctx.call({ httpMethod: 'GET', path: '/emails/{id}' }, { id })
  }

  async create(body: EmailDraft): Promise<LibraryEmail> {
    return this.ctx.call({ httpMethod: 'POST', path: '/emails', hasBody: true }, { body })
  }

  /** `updatedAt`: the e-mail's version read (409 with `data.updatedAt` when another save came first). */
  async update(id: string, body: { name?: string; mainLanguage?: string; updatedAt?: string }): Promise<LibraryEmail> {
    return this.ctx.call({ httpMethod: 'PATCH', path: '/emails/{id}', hasBody: true }, { id, body })
  }

  /** In use: 409 `emails.inUse` with `data.usages`. */
  async remove(id: string): Promise<{ id: string; deleted: boolean }> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/emails/{id}' }, { id })
  }

  /** `updatedAt`: that language version's version read (409 with `data.updatedAt`). */
  async putVersion(id: string, language: string, body: VersionWrite & { updatedAt?: string }): Promise<LibraryEmail> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/emails/{id}/versions/{language}', hasBody: true }, { id, language, body })
  }

  /** F-174: saves one block of a language's version (423 while someone else holds it). */
  async updateVersionBlock(id: string, language: string, blockId: string, block: CampaignBlock): Promise<BlockSaveResult> {
    return this.ctx.call<BlockSaveResult>({ httpMethod: 'PUT', path: '/emails/{id}/versions/{language}/blocks/{blockId}', hasBody: true }, { id, language, blockId, body: { block } })
  }

  async deleteVersion(id: string, language: string): Promise<LibraryEmail> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/emails/{id}/versions/{language}' }, { id, language })
  }

  async usages(id: string): Promise<EmailUsages> {
    return this.ctx.call({ httpMethod: 'GET', path: '/emails/{id}/usages' }, { id })
  }

  async duplicate(id: string): Promise<LibraryEmail> {
    return this.ctx.call({ httpMethod: 'POST', path: '/emails/{id}/duplicate', hasBody: true }, { id, body: {} })
  }

  async preview(id: string, language?: string): Promise<RenderedEmail> {
    return this.ctx.call({ httpMethod: 'POST', path: '/emails/{id}/preview', hasBody: true }, { id, body: language ? { language } : {} })
  }

  /** Sends one e-mail to the signed-in person only (10 an hour). */
  async test(id: string, language?: string): Promise<{ sentTo: string; language?: string }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/emails/{id}/test', hasBody: true }, { id, body: language ? { language } : {} })
  }

  /* ---------------------------------------------------------------- default e-mails (owner and admin) */

  async defaults(): Promise<{ keys: DefaultEmailKey[] }> {
    return this.ctx.call({ httpMethod: 'GET', path: '/email-overrides' })
  }

  async defaultKey(emailKey: string): Promise<DefaultEmailKey> {
    return noteOverride(emailKey, await this.ctx.call<DefaultEmailKey>({ httpMethod: 'GET', path: '/email-overrides/{emailKey}' }, { emailKey }))
  }

  /** Sends the override's version read (`updatedAt`); a stale save (409) asks before saving over it. */
  async setDefaultEnabled(emailKey: string, enabled: boolean): Promise<DefaultEmailKey> {
    const result = await versionedRowWrite(overrideRow(emailKey), (v) => this.ctx.call<DefaultEmailKey>({ httpMethod: 'PATCH', path: '/email-overrides/{emailKey}', hasBody: true }, { emailKey, body: { enabled, ...(v ? { updatedAt: v } : {}) } }))
    return noteOverride(emailKey, result)
  }

  async defaultVersion(emailKey: string, language: string): Promise<DefaultEmailVersion> {
    return noteRowFrom(overrideRow(emailKey, language), await this.ctx.call<DefaultEmailVersion>({ httpMethod: 'GET', path: '/email-overrides/{emailKey}/{language}' }, { emailKey, language }))
  }

  /** A missing locked block: 422 with `data.missingSlots`. */
  async saveDefaultVersion(emailKey: string, language: string, body: VersionWrite & { enabled?: boolean }): Promise<DefaultEmailVersion> {
    return versionedRowWrite(overrideRow(emailKey, language), (v) => this.ctx.call<DefaultEmailVersion>({ httpMethod: 'PUT', path: '/email-overrides/{emailKey}/{language}', hasBody: true }, { emailKey, language, body: { ...body, ...(v ? { updatedAt: v } : {}) } }))
  }

  /** Restores the platform's e-mail in that language (deletes the community's version). */
  /** F-174: saves one block of a default e-mail's language (before the first save: a starter id, `starter-text`, `slot-…`). */
  async updateDefaultBlock(emailKey: string, language: string, blockId: string, block: CampaignBlock): Promise<BlockSaveResult> {
    const result = await this.ctx.call<BlockSaveResult>({ httpMethod: 'PUT', path: '/email-overrides/{emailKey}/{language}/blocks/{blockId}', hasBody: true }, { emailKey, language, blockId, body: { block } })
    noteRowVersion(overrideRow(emailKey, language), result?.updatedAt)
    return result
  }

  async restoreDefault(emailKey: string, language: string): Promise<DefaultEmailKey> {
    const result = await versionedRowWrite(overrideRow(emailKey, language), (v) => this.ctx.call<DefaultEmailKey>({ httpMethod: 'DELETE', path: '/email-overrides/{emailKey}/{language}' }, { emailKey, language, headers: unmodifiedSince(v) }), () => null)
    return noteOverride(emailKey, result)
  }

  async previewDefault(emailKey: string, language: string, body?: VersionInput): Promise<RenderedEmail & { customized?: boolean }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/email-overrides/{emailKey}/{language}/preview', hasBody: true }, { emailKey, language, body: body || {} })
  }

  async testDefault(emailKey: string, language: string): Promise<{ sentTo: string }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/email-overrides/{emailKey}/{language}/test', hasBody: true }, { emailKey, language, body: {} })
  }
}
